'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X, ChevronDown, Sun, Moon } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'About', href: '/about' },
  { label: 'Categories', href: '/categories' },
  { label: 'Timeline', href: '/timeline' },
  { label: 'Winners', href: '/winners' },
  { label: 'Sponsors', href: '/sponsors' },
  { label: 'FAQ', href: '/faq' },
  { label: 'Contact', href: '/contact' },
];

const moreLinks = [
  { label: 'Rules & Guidelines', href: '/rules' },
  { label: 'Privacy Policy', href: '/privacy-policy' },
  { label: 'Terms & Conditions', href: '/terms' },
  { label: 'Refund Policy', href: '/refund-policy' },
];

export default function PublicNav() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const pathname = usePathname();
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (href: string) => pathname === href;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-background/90 backdrop-blur-xl border-b border-gold-subtle shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-3 flex-shrink-0">
          <AppLogo size={36} />
          <span className="font-extrabold text-lg tracking-tight gradient-gold-text hidden sm:block">
            Motion Max
          </span>
        </Link>

        <div className="hidden xl:flex items-center gap-0.5">
          {navLinks?.map((link) => (
            <Link
              key={`nav-${link?.label}`}
              href={link?.href}
              className={`px-3 py-2 text-sm font-medium transition-colors duration-200 rounded-lg ${
                isActive(link?.href)
                  ? 'text-primary bg-primary/10' :'text-muted-foreground hover:text-foreground hover:bg-white/5'
              }`}
            >
              {link?.label}
            </Link>
          ))}
          {/* More dropdown */}
          <div className="relative">
            <button
              onClick={() => setMoreOpen(!moreOpen)}
              className="px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-200 rounded-lg hover:bg-white/5 flex items-center gap-1"
            >
              More <ChevronDown size={14} className={`transition-transform ${moreOpen ? 'rotate-180' : ''}`} />
            </button>
            {moreOpen && (
              <div
                className="absolute top-full right-0 mt-2 w-52 glass-card rounded-xl overflow-hidden shadow-2xl"
                style={{ background: 'rgba(17,17,17,0.97)', border: '1px solid rgba(201,168,76,0.2)' }}
              >
                {moreLinks?.map((link) => (
                  <Link
                    key={`more-${link?.label}`}
                    href={link?.href}
                    onClick={() => setMoreOpen(false)}
                    className="block px-4 py-3 text-sm text-muted-foreground hover:text-primary hover:bg-primary/5 transition-colors"
                  >
                    {link?.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="hidden xl:flex items-center gap-3">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all duration-200"
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <Link
            href="/login"
            className="btn-outline-gold px-5 py-2 rounded-full text-sm font-semibold"
          >
            Sign In
          </Link>
          <Link
            href="/register"
            className="btn-primary px-5 py-2 rounded-full text-sm font-bold"
          >
            Join Now
          </Link>
        </div>

        <div className="xl:hidden flex items-center gap-1">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all duration-200"
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button
            className="p-2 text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="xl:hidden bg-card/97 backdrop-blur-xl border-b border-gold-subtle px-6 py-4 flex flex-col gap-1 max-h-[80vh] overflow-y-auto">
          {navLinks?.map((link) => (
            <Link
              key={`mobile-nav-${link?.label}`}
              href={link?.href}
              className={`py-2.5 px-3 rounded-lg text-sm font-medium transition-colors ${
                isActive(link?.href) ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-primary'
              }`}
              onClick={() => setMenuOpen(false)}
            >
              {link?.label}
            </Link>
          ))}
          <div className="border-t border-gold-subtle pt-2 mt-1">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground/50 px-3 py-2">More</p>
            {moreLinks?.map((link) => (
              <Link
                key={`mobile-more-${link?.label}`}
                href={link?.href}
                className="py-2.5 px-3 rounded-lg text-sm font-medium text-muted-foreground hover:text-primary transition-colors block"
                onClick={() => setMenuOpen(false)}
              >
                {link?.label}
              </Link>
            ))}
          </div>
          <div className="flex gap-3 pt-3 border-t border-gold-subtle mt-1">
            <Link href="/login" className="flex-1 btn-outline-gold py-2.5 rounded-full text-sm font-semibold text-center" onClick={() => setMenuOpen(false)}>
              Sign In
            </Link>
            <Link href="/register" className="flex-1 btn-primary py-2.5 rounded-full text-sm font-bold text-center" onClick={() => setMenuOpen(false)}>
              Join Now
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}